import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { resultsToHighJumpTableData } from 'services/admin/ResultService';
import { getClubRecordsWithAgeGroups, getMeetSummary } from 'api/clubApi';
import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import EventResultPageHeader from 'components/admin/result/EventResultPageHeader';
import EventResultPageOptionButton from 'components/admin/result/EventResultPageOptionButton';
import ResultDataTable from 'components/admin/result/ResultDataTable';
import ResultEditPopup from 'components/admin/ResultEditPopup';
import { convertDateTime } from 'utils/time';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import EventPopup from 'components/admin/EventPopup';
import { useEventRoundData } from 'hooks/useEventRoundData';

const EventViewHighJumpResults = ({
  clubId,
  meetId,
  eventType,
  eventID,
  isOpenEditPopup,
  isOpenEditEventPopup,
}) => {
  const navigate = useNavigate();
  const userPermissionLevel = getUserPermissionLevel();

  const { data, loading, error, refetch } = useEventRoundData(
    { clubID: clubId, meetID: meetId, eventID },
    true
  );

  const [showEditPopup, setShowEditPopup] = useState(isOpenEditPopup);
  const [showEditEventPopup, setShowEditEventPopup] = useState(
    isOpenEditEventPopup
  );
  const [meet, setMeet] = useState(null);
  const [fetchingMeet, setFetchingMeet] = useState(true);
  const [clubRecords, setClubRecords] = useState([]);

  const metadata = data?.metadata;
  const tableRows = useMemo(
    () =>
      data?.results ? resultsToHighJumpTableData(data.results, eventType) : [],
    [data?.results, eventType]
  );

  const dataTypeOptions =
    metadata?.siblingRounds?.map((r) => ({
      label: `${r.roundType} ${r.roundNumber}`,
      type: r.roundType,
      value: r.eventID,
    })) || [];

  const fetchMeet = useCallback(async () => {
    try {
      const response = await getMeetSummary(clubId, meetId);
      setMeet(response.data);
    } catch (err) {
      console.log('[Results] Fetch Meets error: ', err);
    }
  }, [clubId, meetId]);

  // Stable string of distinct athlete ages — only changes when the actual
  // age set differs (rare across sibling rounds of the same event), so
  // fetchClubRecords doesn't refire on every tab switch.
  const distinctAgesQuery = useMemo(() => {
    if (!tableRows.length) return '';
    const ages = [
      ...new Set(
        tableRows
          .map((r) => r.athleteAge || r.age)
          .filter((age) => age && age > 0)
          .map(String)
      ),
    ].sort();
    return ages.join(', ');
  }, [tableRows]);

  const fetchClubRecords = useCallback(async () => {
    if (!clubId || !eventType || !metadata?.gender || !distinctAgesQuery) {
      return;
    }
    const response = await getClubRecordsWithAgeGroups(
      clubId,
      eventType,
      distinctAgesQuery,
      metadata.gender
    ).catch((e) => {
      console.error(e);
      return null;
    });
    if (response) {
      setClubRecords(response.data);
    }
  }, [clubId, eventType, metadata?.gender, distinctAgesQuery]);

  useEffect(() => {
    fetchMeet().then(() => setFetchingMeet(false));
  }, [fetchMeet]);

  useEffect(() => {
    fetchClubRecords();
  }, [fetchClubRecords]);

  const handleRoundOptionChange = (name, value) => {
    const sibling = metadata?.siblingRounds?.find((r) => r.eventID === value);
    if (!sibling) return;
    navigate(
      routes.admin.eventRound.url(clubId, meetId, eventType, sibling.eventID)
    );
  };

  const openEditPopup = () => setShowEditPopup(true);
  const closeEditPopup = () => setShowEditPopup(false);
  const openEditEventPopup = () => setShowEditEventPopup(true);
  const closeEditEventPopup = () => setShowEditEventPopup(false);

  // After results are saved or deleted in the dialog: fetch this event again
  // (one request, no page reload). Returns a promise so the dialog can wait
  // for the saved results before it lets another change be saved.
  const refreshResults = () => refetch();

  return (
    <>
      <MetaTags title="SplitFast | Results" />
      <AdminTablePageLayout loading={fetchingMeet || (loading && !data)}>
        <EventResultPageHeader
          title={
            metadata
              ? `${metadata.eventType} ${metadata.eventAgeGroup} ${metadata.gender}`
              : 'Results'
          }
          suffix={
            metadata?.publishingStatus === 'Preliminary' ? 'Preliminary' : ''
          }
          viewLabel="Round Results"
          clubRecords={clubRecords}
          eventType={eventType}
          meet={meet}
          clubId={clubId}
        />

        <EventResultPageOptionButton
          handleEditResult={
            userPermissionLevel >= UserPermissionLevel.Official
              ? openEditPopup
              : undefined
          }
          handleEditEvent={
            userPermissionLevel >= UserPermissionLevel.Official
              ? openEditEventPopup
              : undefined
          }
          isAdd={tableRows.length === 0}
          showEditResultButton={
            userPermissionLevel >= UserPermissionLevel.Official
          }
          showEditEventButton={
            userPermissionLevel >= UserPermissionLevel.Official
          }
        />

        {error ? (
          <div style={{ textAlign: 'center' }}>
            {error?.response?.data?.error || 'Event not found'}
          </div>
        ) : tableRows.length || metadata ? (
          // The event is known (metadata) even when it has no results yet:
          // show its round tabs and an empty table.
          <ResultDataTable
            dataTypeOptions={dataTypeOptions}
            optionValues={{ dataType: eventID }}
            handleOptionChange={handleRoundOptionChange}
            results={tableRows}
            eventType={eventType}
            showEmptyTable
            emptyMessage={
              userPermissionLevel >= UserPermissionLevel.Official
                ? 'No results yet. Use + Add Results to enter them.'
                : 'No results yet.'
            }
          />
        ) : (
          <div style={{ textAlign: 'center' }}>
            {loading ? 'Loading results...' : 'No results found'}
          </div>
        )}
      </AdminTablePageLayout>
      {userPermissionLevel >= UserPermissionLevel.Official && (
        <ResultEditPopup
          results={tableRows}
          clubID={clubId}
          meetID={meetId}
          viewType="event-view"
          eventType={eventType}
          ageGroup={metadata?.eventAgeGroup}
          gender={metadata?.gender}
          roundType={metadata?.roundType}
          eventID={eventID}
          preliminary={metadata?.publishingStatus === 'Preliminary'}
          title={
            metadata
              ? `Results for ${metadata.eventType} ${metadata.eventAgeGroup} ${metadata.gender}`
              : 'Results'
          }
          subtitle={meet ? convertDateTime(meet?.meetDate || '').date : ''}
          showPopup={showEditPopup}
          closePopup={closeEditPopup}
          refreshResults={refreshResults}
        />
      )}
      {userPermissionLevel >= UserPermissionLevel.Official && (
        <EventPopup
          mode="edit"
          eventID={eventID}
          clubID={clubId}
          meetID={meetId}
          showPopup={showEditEventPopup}
          closePopup={closeEditEventPopup}
          onSuccess={refreshResults}
        />
      )}
    </>
  );
};

EventViewHighJumpResults.propTypes = {
  clubId: PropTypes.string,
  meetId: PropTypes.string,
  eventType: PropTypes.string,
  eventID: PropTypes.string,
  isOpenEditPopup: PropTypes.bool,
  isOpenEditEventPopup: PropTypes.bool,
};

export default EventViewHighJumpResults;
